import { useEffect, useState } from "react";
import axios from "axios";
import { Heart, MessageCircle, Sparkles, Share2 } from "lucide-react";
import PostCard from "@/components/app/PostCard";
import { posts as mockPosts, currentUser } from "@/lib/mockData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tsRel = (s) => {
  if (!s) return "";
  const d = new Date(s);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "ahora";
  if (sec < 3600) return `hace ${Math.floor(sec / 60)} min`;
  if (sec < 86400) return `hace ${Math.floor(sec / 3600)} h`;
  return d.toLocaleDateString("es", { day: "2-digit", month: "short" });
};

export default function Feed({ mode, refreshKey = 0 }) {
  const list = mockPosts[mode] || [];
  const [userPosts, setUserPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/posts/feed`, { params: { mode, limit: 30 } })
      .then((r) => setUserPosts(r.data || []))
      .catch(() => {});
  }, [mode, refreshKey]);

  return (
    <div data-testid={`feed-${mode}`}>
      <div className="mb-5 px-1 flex items-center gap-3">
        <span className="font-serif text-2xl md:text-3xl tracking-tight">
          {mode === "vinculos" ? "Modo Vínculos" : "Modo Horizonte"}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">
          {mode === "vinculos" ? "orden cronológico · sin algoritmo" : "descubrimiento · IA explicable"}
        </span>
      </div>

      {userPosts.map((p) => <RealPostCard key={p.id} post={p} />)}

      {list.map((p) => (
        <PostCard key={p.id} post={p} mode={mode} />
      ))}

      <div className="text-center py-8">
        <button className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-mid hover:text-coral transition">
          Has llegado al final · Volver arriba ↑
        </button>
      </div>
    </div>
  );
}

function RealPostCard({ post }) {
  const [likes, setLikes] = useState(post.likes || 0);
  const [liking, setLiking] = useState(false);

  const like = async () => {
    if (liking) return;
    setLiking(true);
    setLikes((n) => n + 1);
    try {
      await axios.post(`${API}/posts/${post.id}/like`);
    } catch {
      setLikes((n) => n - 1);
    } finally {
      setLiking(false);
    }
  };

  const isMe = post.user_id === currentUser.id;

  return (
    <article data-testid={`real-post-${post.id}`} className="border hairline bg-cream-card rounded-2xl p-5 mb-4">
      <header className="flex items-center gap-3 mb-3">
        <img src={post.user_avatar || currentUser.avatar} alt="" className="w-10 h-10 rounded-full ring-2 ring-coral/20" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium truncate">{post.user_name || "Tú"}</p>
            {isMe && (
              <span className="text-[9px] font-mono uppercase tracking-wider text-coral bg-coral/10 px-1.5 py-0.5 rounded-full">tu post</span>
            )}
            {post.signature && (
              <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider bg-gradient-to-r from-coral to-ink text-cream px-1.5 py-0.5 rounded-full">
                <Sparkles className="w-2.5 h-2.5" /> Original
              </span>
            )}
          </div>
          <p className="text-[11px] font-mono uppercase tracking-wider text-ink-mid">{tsRel(post.created_at)}</p>
        </div>
      </header>

      {post.text && (
        <p className="text-[15px] leading-relaxed whitespace-pre-wrap mb-3">{post.text}</p>
      )}

      {post.photo_data_url && (
        <img
          src={post.photo_data_url}
          alt=""
          style={post.filter_css ? { filter: post.filter_css } : undefined}
          className="w-full max-h-[640px] object-cover rounded-xl border hairline"
          data-testid={`real-post-photo-${post.id}`}
        />
      )}

      <footer className="mt-3 flex items-center gap-1 pt-2 border-t hairline">
        <button
          onClick={like}
          data-testid={`real-post-like-${post.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-ink-mid hover:text-coral hover:bg-coral/10 transition"
        >
          <Heart className="w-3.5 h-3.5" /> {likes}
        </button>
        <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-ink-mid hover:text-coral hover:bg-coral/10 transition">
          <MessageCircle className="w-3.5 h-3.5" /> {post.comments || 0}
        </button>
        <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-ink-mid hover:text-coral hover:bg-coral/10 transition ml-auto">
          <Share2 className="w-3.5 h-3.5" /> Compartir
        </button>
      </footer>
    </article>
  );
}
